import {
  computed,
  ref,
  watchEffect,
  ComputedRef,
} from 'vue'

export default function useSlider({
  root,
  container,
  items,
  paddingLeft = 0,
}: {
  root: HTMLDivElement
  container: HTMLDivElement
  items: ComputedRef<HTMLDivElement[]>
  paddingLeft?: number
}) {
  try {
    let isDown = false
    let startX = 0
    const paddingLeftShifted = paddingLeft + 1
    const offsets = computed(() => items.value.map(el => el.offsetLeft - container.offsetLeft))

    const hasUserInteracted = ref(false)
    const areButtonsVisible = ref(false)
    const scrollLeftSaved = ref(0)
    const actualScrollLeft = ref(0)

    const closestItemIndex = computed(
      () => offsets.value.findLastIndex(offset => actualScrollLeft.value >= offset - paddingLeftShifted),
    )

    const isFullyScrolledToLeft = computed(() => actualScrollLeft.value === 0)
    const isFullyScrolledToRight = computed(
      () => container.scrollWidth - actualScrollLeft.value <= container.clientWidth,
    )

    const isRightArrowVisible = computed(() => areButtonsVisible.value && !isFullyScrolledToRight.value)
    const isLeftArrowVisible = computed(() => areButtonsVisible.value && !isFullyScrolledToLeft.value)

    const scrollOffsetForNextItem = computed(() => {
      const nextIndex = closestItemIndex.value + 1

      return offsets.value[nextIndex >= offsets.value.length ? closestItemIndex.value : nextIndex]
    })

    const scrollOffsetForPrevItem = computed(() => {
      const prevIndex = closestItemIndex.value - 1

      return offsets.value[prevIndex < 0 ? closestItemIndex.value : prevIndex]
    })

    watchEffect(() => {
      actualScrollLeft.value = scrollLeftSaved.value
    })

    function addEventListeners() { // eslint-disable-line no-inner-declarations
      root.addEventListener('mouseenter', () => {
        areButtonsVisible.value = true
        hasUserInteracted.value = true
        actualScrollLeft.value = container.scrollLeft
      })

      root.addEventListener('mouseleave', () => {
        areButtonsVisible.value = false
      })

      container.addEventListener('mousedown', e => {
        isDown = true
        startX = e.pageX - container.offsetLeft
        scrollLeftSaved.value = container.scrollLeft
      })

      window.addEventListener('mouseup', () => {
        if (!isDown) {
          return
        }

        isDown = false
        scrollLeftSaved.value = container.scrollLeft
      })

      window.addEventListener('mousemove', e => {
        if (!isDown) {
          return
        }

        e.preventDefault()

        const x = e.pageX - container.offsetLeft
        const walk = x - startX

        container.scrollLeft = scrollLeftSaved.value - walk
        actualScrollLeft.value = container.scrollLeft
      })
    }

    function scroll(value: number) { // eslint-disable-line no-inner-declarations
      container.scroll({
        left: value - paddingLeftShifted,
        behavior: 'smooth',
      })

      scrollLeftSaved.value = value
    }

    function scrollToNextItem() { // eslint-disable-line no-inner-declarations
      scroll(scrollOffsetForNextItem.value)
    }

    function scrollToPrevItem() { // eslint-disable-line no-inner-declarations
      scroll(scrollOffsetForPrevItem.value)
    }

    function scrollToItemByIndex(index: number, centered?: boolean) { // eslint-disable-line no-inner-declarations
      if (centered) {
        const offsetCenterSlider = (container.clientWidth / 2) - (items.value[index].clientWidth / 2)
        const scrollTo = offsets.value[index] - offsetCenterSlider

        scroll(scrollTo > 0 ? scrollTo : 0)

        return
      }

      scroll(offsets.value[index])
    }

    function isClick(pageX: number) { // eslint-disable-line no-inner-declarations
      return pageX - container.offsetLeft === startX
    }

    return {
      hasUserInteracted,
      isLeftArrowVisible,
      isRightArrowVisible,
      isFullyScrolledToLeft,
      isFullyScrolledToRight,
      addEventListeners,
      scrollToNextItem,
      scrollToPrevItem,
      scrollToItemByIndex,
      isClick,
    }
  } catch {
    return {
      hasUserInteracted: false,
      isLeftArrowVisible: false,
      isRightArrowVisible: false,
      isFullyScrolledToLeft: true,
      isFullyScrolledToRight: true,
      addEventListeners: () => {},
      scrollToNextItem: () => {},
      scrollToPrevItem: () => {},
      scrollToItemByIndex: () => {},
      isClick: () => {},
    }
  }
}
